import { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import './FCHero.css'

const API = process.env.REACT_APP_API_URL || ""

function useHeroImage() {
    const [urls, setUrls] = useState({})

    useEffect(() => {
        fetch(`${API}/api/media/fc-hero`)
            .then(res => res.ok ? res.json() : {})
            .then(map => setUrls(map))
            .catch(() => { })
    }, [])

    return urls
}

/* ─── HUD: Attitude Indicator ─────────────────────────────────────────────── */
function AttitudeRing() {
    return (
        <svg viewBox="0 0 160 160" className="hud-attitude" xmlns="http://www.w3.org/2000/svg">
            {/* Outer ring */}
            <circle cx="80" cy="80" r="72" fill="none" stroke="rgba(93,23,158,0.25)" strokeWidth="1" />
            <circle cx="80" cy="80" r="72" fill="none" stroke="rgba(93,23,158,0.6)"
                strokeWidth="1.2" strokeDasharray="6 4" className="hud-ring-spin" />

            {/* Inner rings */}
            <circle cx="80" cy="80" r="56" fill="none" stroke="rgba(93,23,158,0.2)" strokeWidth="0.8" />
            <circle cx="80" cy="80" r="40" fill="none" stroke="rgba(93,23,158,0.35)" strokeWidth="0.8" />

            {/* Horizon line */}
            <line x1="16" y1="80" x2="144" y2="80" stroke="rgba(168,85,247,0.5)" strokeWidth="0.8" strokeDasharray="3 5" />
            <line x1="56" y1="80" x2="104" y2="80" stroke="rgba(168,85,247,0.9)" strokeWidth="1.5" />

            {/* Pitch marks */}
            {[-20, -10, 10, 20].map((deg, i) => {
                const y = 80 + deg * 1.2
                const w = Math.abs(deg) === 20 ? 30 : 18
                return (
                    <g key={i}>
                        <line x1={80 - w} y1={y} x2={80 + w} y2={y}
                            stroke="rgba(93,23,158,0.45)" strokeWidth="0.8" />
                        <text x={80 + w + 5} y={y + 3.5}
                            fill="rgba(93,23,158,0.55)" fontSize="7" fontFamily="IBM Plex Mono">
                            {Math.abs(deg)}
                        </text>
                    </g>
                )
            })}

            {/* Aircraft cross */}
            <line x1="50" y1="80" x2="68" y2="80" stroke="#a855f7" strokeWidth="2" />
            <line x1="92" y1="80" x2="110" y2="80" stroke="#a855f7" strokeWidth="2" />
            <circle cx="80" cy="80" r="3.5" fill="none" stroke="#a855f7" strokeWidth="1.5" />
            <circle cx="80" cy="80" r="1" fill="#a855f7" />

            {/* Roll ticks */}
            {[0, 30, 45, 60, 90, 120, 135, 150, 180, 210, 225, 240, 270, 300, 315, 330].map((angle, i) => {
                const rad = (angle - 90) * (Math.PI / 180)
                const len = [0, 90, 180, 270].includes(angle) ? 10 : 5
                const r1 = 72, r2 = 72 - len
                return (
                    <line key={i}
                        x1={80 + r1 * Math.cos(rad)} y1={80 + r1 * Math.sin(rad)}
                        x2={80 + r2 * Math.cos(rad)} y2={80 + r2 * Math.sin(rad)}
                        stroke="rgba(168,85,247,0.5)" strokeWidth="1" />
                )
            })}

            {/* Live label */}
            <text x="80" y="148" textAnchor="middle"
                fill="rgba(93,23,158,0.7)" fontSize="7" fontFamily="IBM Plex Mono" letterSpacing="3">
                ATT
            </text>
        </svg>
    )
}

/* ─── HUD: PWM Output Bars ────────────────────────────────────────────────── */
function PwmBars() {
    const values = [
        { label: 'M1', pct: 72, cls: 'pwm-bar-1' },
        { label: 'M2', pct: 68, cls: 'pwm-bar-2' },
        { label: 'M3', pct: 75, cls: 'pwm-bar-3' },
        { label: 'M4', pct: 70, cls: 'pwm-bar-4' },
    ]
    return (
        <div className="hud-pwm">
            <span className="hud-label">PWM OUTPUT</span>
            <div className="pwm-grid">
                {values.map((v) => (
                    <div key={v.label} className="pwm-row">
                        <span className="pwm-motor">{v.label}</span>
                        <div className="pwm-track">
                            <div className={`pwm-fill ${v.cls}`} style={{ '--pct': `${v.pct}%` }} />
                        </div>
                        <span className="pwm-val">{v.pct}%</span>
                    </div>
                ))}
            </div>
        </div>
    )
}

/* ─── HUD: Gyro Readout ───────────────────────────────────────────────────── */
function GyroReadout() {
    return (
        <div className="hud-gyro">
            <span className="hud-label">GYRO  IMU</span>
            <div className="gyro-rows">
                {[
                    { axis: 'ROLL', val: '+2.4°', cls: 'gyro-r' },
                    { axis: 'PITCH', val: '-1.1°', cls: 'gyro-p' },
                    { axis: 'YAW', val: '+0.3°', cls: 'gyro-y' },
                ].map((g) => (
                    <div key={g.axis} className="gyro-row">
                        <span className="gyro-axis">{g.axis}</span>
                        <span className={`gyro-bar-wrap`}>
                            <span className={`gyro-bar ${g.cls}`} />
                        </span>
                        <span className="gyro-val">{g.val}</span>
                    </div>
                ))}
            </div>
            <div className="gyro-status">
                <span className="gyro-dot" />
                <span className="gyro-ok">3-AXIS LOCKED</span>
            </div>
        </div>
    )
}

/* ─── HUD: Voltage Badge ──────────────────────────────────────────────────── */
function VoltageBadge() {
    return (
        <div className="hud-voltage">
            <span className="hud-label">INPUT</span>
            <span className="volt-value">5<span className="volt-unit">V</span></span>
            <div className="volt-bar">
                <div className="volt-fill" />
            </div>
        </div>
    )
}

/* ─── Main Hero ───────────────────────────────────────────────────────────── */
export default function FCHero() {
    const urls = useHeroImage()
    const pcbImage = urls['fc-hero']

    const heroRef = useRef(null)
    const eyebrowRef = useRef(null)
    const titleRef = useRef(null)
    const taglineRef = useRef(null)
    const ctaRef = useRef(null)
    const badgesRef = useRef(null)
    const pcbRef = useRef(null)
    const glowRef = useRef(null)
    const hudLeftRef = useRef(null)
    const hudRightRef = useRef(null)

    useEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ defaults: { ease: 'expo.out' } })

            // Traces draw in
            gsap.fromTo('.fch-trace', { opacity: 0 }, {
                opacity: 1, duration: 2, stagger: 0.08, ease: 'power2.out', delay: 0.2
            })

            // Staggered reveal sequence
            tl.fromTo(eyebrowRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7 }, 0.3)
                .fromTo(titleRef.current.querySelectorAll('.fch-word'),
                    { opacity: 0, y: 80, skewY: 6 },
                    { opacity: 1, y: 0, skewY: 0, duration: 1, stagger: 0.1 }, 0.5)
                .fromTo(taglineRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.8 }, 1.1)
                .fromTo(ctaRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7 }, 1.3)
                .fromTo(badgesRef.current.children, { opacity: 0, y: 10 },
                    { opacity: 1, y: 0, duration: 0.5, stagger: 0.1 }, 1.4)

            // PCB float in
            tl.fromTo(pcbRef.current,
                { opacity: 0, scale: 0.88, y: 40 },
                { opacity: 1, scale: 1, y: 0, duration: 1.2, ease: 'expo.out' }, 0.6)

            // PCB glow pulse
            gsap.to(glowRef.current, {
                opacity: 0.75, scale: 1.08, duration: 2.5,
                ease: 'sine.inOut', yoyo: true, repeat: -1
            })

            // PCB float loop
            gsap.to(pcbRef.current, {
                y: -14, duration: 3.2, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 1.5
            })

            // HUD panels
            tl.fromTo(hudLeftRef.current, { opacity: 0, x: -30 }, { opacity: 1, x: 0, duration: 0.8 }, 1.0)
                .fromTo(hudRightRef.current, { opacity: 0, x: 30 }, { opacity: 1, x: 0, duration: 0.8 }, 1.0)

        }, heroRef)
        return () => ctx.revert()
    }, [])

    return (
        <section ref={heroRef} className="fch-wrap">

            {/* ── Circuit trace background ── */}
            <svg className="fch-traces" viewBox="0 0 1440 800" preserveAspectRatio="xMidYMid slice"
                xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <g stroke="rgba(93,23,158,0.12)" strokeWidth="1" fill="none">
                    <path className="fch-trace" d="M0,200 H300 V400 H600" />
                    <path className="fch-trace" d="M0,400 H180 V250 H420 V500 H720" />
                    <path className="fch-trace" d="M200,0 V180 H500 V320 H800" />
                    <path className="fch-trace" d="M1440,180 H1100 V360 H820" />
                    <path className="fch-trace" d="M1440,500 H1200 V280 H960" />
                    <path className="fch-trace" d="M700,800 V600 H900 V420" />
                    <path className="fch-trace" d="M400,800 V680 H200 V520 H0" />
                </g>
                {/* Via pads */}
                {[[300, 400], [600, 400], [180, 250], [420, 500], [500, 320], [1100, 360], [1200, 280], [900, 420]].map(([x, y], i) => (
                    <g key={i}>
                        <circle cx={x} cy={y} r="4" fill="none" stroke="rgba(93,23,158,0.2)" strokeWidth="1" />
                        <circle cx={x} cy={y} r="1.5" fill="rgba(93,23,158,0.3)" />
                    </g>
                ))}
                {/* Scan line */}
                <line className="fch-scanline" x1="0" y1="0" x2="1440" y2="0"
                    stroke="rgba(168,85,247,0.08)" strokeWidth="1" />
            </svg>

            {/* ── Dot grid overlay ── */}
            <div className="fch-dotgrid" aria-hidden="true" />

            <div className="fch-inner">

                {/* ── LEFT COLUMN ── */}
                <div className="fch-left">
                    <div ref={eyebrowRef} className="fch-eyebrow" style={{ opacity: 0 }}>
                        <span className="fch-dot" />
                        FLIGHT CONTROLLER
                        <span className="fch-slash">/</span>
                        MOCK FLY FC
                    </div>

                    <h1 ref={titleRef} className="fch-title">
                        <span className="fch-word">MOCK</span>
                        <span className="fch-word fch-word--accent">FLY</span>
                        <span className="fch-word fch-word--sm">FLIGHT</span>
                        <span className="fch-word fch-word--sm">CONTROLLER</span>
                    </h1>

                    <p ref={taglineRef} className="fch-tagline" style={{ opacity: 0 }}>
                        Your first board to the sky — built to train,<br />
                        tune, and take flight.
                    </p>

                    <div ref={ctaRef} className="fch-cta-row" style={{ opacity: 0 }}>
                        <button className="fch-btn-primary">
                            <span>EXPLORE SPECS</span>
                            <span className="fch-btn-arrow">→</span>
                        </button>
                        <button className="fch-btn-ghost">OUR STORY</button>
                    </div>

                    <div ref={badgesRef} className="fch-badges">
                        <span className="fch-badge">
                            <span className="fch-badge-dot" />READY TO FLY
                        </span>
                        <span className="fch-badge">5V INPUT</span>
                        <span className="fch-badge">4 PWM OUT</span>
                        <span className="fch-badge fch-badge--stem">
                            <span className="fch-badge-dot" />STEM READY
                        </span>
                    </div>
                </div>

                {/* ── RIGHT COLUMN — PCB + HUD ── */}
                <div className="fch-right">

                    {/* HUD left panel */}
                    <div ref={hudLeftRef} className="fch-hud fch-hud--left" style={{ opacity: 0 }}>
                        <AttitudeRing />
                        <VoltageBadge />
                    </div>

                    {/* PCB */}
                    <div className="fch-pcb-wrap">
                        <div ref={glowRef} className="fch-pcb-glow" />
                        <div ref={pcbRef} className="fch-pcb-frame" style={{ opacity: 0 }}>
                            {/* Corner brackets */}
                            <span className="fch-corner fch-corner--tl" />
                            <span className="fch-corner fch-corner--tr" />
                            <span className="fch-corner fch-corner--bl" />
                            <span className="fch-corner fch-corner--br" />

                            {pcbImage
                                ? <img src={pcbImage} alt="Mock Fly Flight Controller PCB" className="fch-pcb-img" />
                                : <div className="fch-pcb-placeholder">
                                    <span className="fch-pcb-label">MOCK FLY FC</span>
                                </div>
                            }

                            {/* Scan overlay */}
                            <div className="fch-pcb-scan" />
                        </div>

                        {/* Floating data tags */}
                        <div className="fch-tag fch-tag--gyro">
                            <span className="fch-tag-dot" />6-AXIS IMU
                        </div>
                        <div className="fch-tag fch-tag--ppm">
                            <span className="fch-tag-dot" />PPM RC INPUT
                        </div>
                        <div className="fch-tag fch-tag--pid">
                            <span className="fch-tag-dot" />NO PID TUNING
                        </div>
                    </div>

                    {/* HUD right panel */}
                    <div ref={hudRightRef} className="fch-hud fch-hud--right" style={{ opacity: 0 }}>
                        <PwmBars />
                        <GyroReadout />
                    </div>

                </div>
            </div>

            {/* ── Bottom bar ── */}
            <div className="fch-bottom-bar">
                <span className="fch-mono">AVIATRICKS</span>
                <span className="fch-bar-line" />
                <span className="fch-mono">INDIGENOUS UAV HARDWARE</span>
                <span className="fch-bar-line" />
                <span className="fch-mono fch-blink">● MADE IN INDIA</span>
            </div>

        </section>
    )
}